// Which roles a candidate can be put on, company by company — T38a (14 Sep 2026); from every CRM
// tab, with a role typed once, since T38b (16 Sep 2026).
//
// Natanel, T38a: pick "a client's company/companies that this candidate is for", and each company
// "gives me options of the roles that client company is hiring for", so one person can be on
// several roles at several companies at once. T38b, for a commercial leader being introduced to
// eight companies on the Sales tab: "an initial dropdown of 'clients', 'potential clients' and
// 'sales'. one is picked, then comes the list of companies under each which more than one can be
// selected, then they have the ability to type the role if it isnt there". The new-candidate form
// and the candidate page's Add to roles draw the same picker (screens/RolesPicker.tsx) from this
// module, which is pure so its order and its plan are tested rather than eyeballed.
//
// WHAT IS OFFERED. Every company on the three tabs but a lost one, with roles or without, and every
// role that is not closed. A role at a company not in Clients is not live (T58): the person goes on
// its pipeline, but no invitation code can be issued on it until the company signs, and client-vera
// is not handed it (packages/agent/src/client-vera/loader.ts).
//
// A ROLE TYPED ONCE is offered at every company picked. It starts ticked where the company has no
// open role, or has one by that name, and unticked where the company has other roles — so a role
// typed for eight prospects is never made at a client whose own role was why it was picked. Where
// the company already has a role by that name (the equal key, as the New search form's duplicate
// check reads it: src/duplicates.ts), the person goes on that role and no second copy is made.
import type { ClientSummary, SearchSummary } from '@vera/bff';
import { isLiveSearch, type ClientStatus } from '@vera/db/client-status';
import { findByName, type NameMatch } from '@vera/db/match-key';
import { mandateLabel } from './submission.ts';

/** The key of the group holding roles that are on no company at all. */
export const NO_COMPANY = 'no-company';

/** The picker's tabs, in the order the nav's CRM group lists them. */
export const PICKER_TABS: readonly ClientStatus[] = ['client', 'potential', 'sales'];

export interface RoleOption {
  id: string;
  title: string;
}

export interface CompanyRoles {
  /** The company's id, or `NO_COMPANY`. */
  key: string;
  name: string;
  /** The CRM tab it is on. The roles on no company sit under Clients: they are live, as a client's are. */
  tab: ClientStatus;
  /** False while the company is not in Clients (T58): its roles are not live. */
  live: boolean;
  /** Its open roles, by title. */
  roles: RoleOption[];
}

/**
 * Every company a person can be put forward to, with its open roles: tab by tab in the nav's
 * order, each tab by name, the roles on no company last under Clients.
 */
export function pickerGroups(
  companies: readonly ClientSummary[],
  searches: readonly SearchSummary[],
): CompanyRoles[] {
  const groups = new Map<string, CompanyRoles>();
  for (const company of companies) {
    if (company.lostAt !== null) continue;
    groups.set(company.id, {
      key: company.id,
      name: company.name,
      tab: company.status,
      live: isLiveSearch({ clientId: company.id, clientStatus: company.status }),
      roles: [],
    });
  }
  for (const search of searches) {
    if (search.status === 'closed') continue;
    const role = { id: search.id, title: search.title };
    if (search.clientId === null) {
      const none = groups.get(NO_COMPANY) ?? {
        key: NO_COMPANY,
        name: 'No company',
        tab: 'client' as const,
        live: true,
        roles: [],
      };
      none.roles.push(role);
      groups.set(NO_COMPANY, none);
      continue;
    }
    // A role whose company is lost, or not in the company list yet, is not offered: nor is its company.
    groups.get(search.clientId)?.roles.push(role);
  }
  const list = [...groups.values()];
  for (const group of list) group.roles.sort((a, b) => a.title.localeCompare(b.title));
  return list.sort(
    (a, b) =>
      PICKER_TABS.indexOf(a.tab) - PICKER_TABS.indexOf(b.tab) ||
      Number(a.key === NO_COMPANY) - Number(b.key === NO_COMPANY) ||
      a.name.localeCompare(b.name),
  );
}

/** The companies holding any of these roles, in the picker's order. */
export function companiesHolding(
  groups: readonly CompanyRoles[],
  roleIds: Iterable<string>,
): string[] {
  const wanted = new Set(roleIds);
  return groups
    .filter((group) => group.roles.some((role) => wanted.has(role.id)))
    .map((group) => group.key);
}

/** What a recruiter has picked. The picker draws it; `planRoles` reads it. */
export interface RolesChoice {
  /** Companies picked by a tap. A company holding a ticked role is picked whatever this says. */
  companies: ReadonlySet<string>;
  /** Roles already on file, ticked. */
  roleIds: ReadonlySet<string>;
  /** The role typed once; blank for none. */
  newTitle: string;
  /** Companies where the typed role was ticked by hand, over where it starts. */
  newOn: ReadonlySet<string>;
  /** Companies where it was unticked by hand. */
  newOff: ReadonlySet<string>;
}

/** Nothing picked — or, from a search page, its one role ticked. */
export function initialChoice(roleIds: Iterable<string> = []): RolesChoice {
  return {
    companies: new Set(),
    roleIds: new Set(roleIds),
    newTitle: '',
    newOn: new Set(),
    newOff: new Set(),
  };
}

/** The companies picked, in the picker's order. */
export function pickedCompanies(
  groups: readonly CompanyRoles[],
  choice: RolesChoice,
): CompanyRoles[] {
  const holding = new Set(companiesHolding(groups, choice.roleIds));
  return groups.filter((group) => choice.companies.has(group.key) || holding.has(group.key));
}

/** The tab to open on: the first one holding a ticked role, else Clients. */
export function openingTab(groups: readonly CompanyRoles[], choice: RolesChoice): ClientStatus {
  return pickedCompanies(groups, choice)[0]?.tab ?? 'client';
}

/** A typed title as it would be saved: trimmed, its runs of spaces one. */
export const cleanTitle = (title: string): string => title.trim().replace(/\s+/g, ' ');

/** The typed role at one company: what it matches there, and whether it is ticked. */
export interface TypedRole {
  title: string;
  /** A role the company already has under that name (`equal`), or under nearly that name (`close`). */
  match: NameMatch<RoleOption> | null;
  on: boolean;
}

/** Null when nothing is typed, or at the roles on no company — a new role there has no company to be on. */
export function typedRoleAt(group: CompanyRoles, choice: RolesChoice): TypedRole | null {
  const title = cleanTitle(choice.newTitle);
  if (title === '' || group.key === NO_COMPANY) return null;
  const found = findByName(
    title,
    group.roles.map((role) => ({ name: role.title, role })),
  );
  const match = found ? { closeness: found.closeness, item: found.item.role } : null;
  const byDefault = group.roles.length === 0 || match?.closeness === 'equal';
  const on = choice.newOn.has(group.key) || (byDefault && !choice.newOff.has(group.key));
  return { title, match, on };
}

/** Is this role ticked — by hand, or because it is the role typed, under its own name? */
export function roleTicked(group: CompanyRoles, role: RoleOption, choice: RolesChoice): boolean {
  if (choice.roleIds.has(role.id)) return true;
  const typed = typedRoleAt(group, choice);
  return (
    typed !== null &&
    typed.on &&
    typed.match?.closeness === 'equal' &&
    typed.match.item.id === role.id
  );
}

export interface RolesPlan {
  /** Roles on file to put the person on. */
  add: string[];
  /** Roles to make first, the person then going on each. */
  create: { clientId: string; title: string }[];
}

/**
 * What Add does: never a role they are already on, never the same role twice. A role handed in that
 * the picker does not offer — a closed Search's own "Enter a new candidate" link — is kept, as the
 * form kept it before T38b: nothing on screen could have unticked it.
 */
export function planRoles(
  groups: readonly CompanyRoles[],
  choice: RolesChoice,
  already: ReadonlySet<string> = new Set(),
): RolesPlan {
  const plan: RolesPlan = { add: [], create: [] };
  const put = (roleId: string) => {
    if (!already.has(roleId) && !plan.add.includes(roleId)) plan.add.push(roleId);
  };
  for (const group of pickedCompanies(groups, choice)) {
    for (const role of group.roles) if (choice.roleIds.has(role.id)) put(role.id);
    const typed = typedRoleAt(group, choice);
    if (!typed?.on) continue;
    if (typed.match?.closeness === 'equal') put(typed.match.item.id);
    else plan.create.push({ clientId: group.key, title: typed.title });
  }
  const offered = new Set(groups.flatMap((group) => group.roles.map((role) => role.id)));
  for (const roleId of choice.roleIds) if (!offered.has(roleId)) put(roleId);
  return plan;
}

/** How many roles the plan puts the person on — the number on the Add button. */
export const planSize = (plan: RolesPlan): number => plan.add.length + plan.create.length;

/** One badge in a candidate's header: a role, or one title at several companies at one stage. */
export interface RoleBadge<S extends string = string> {
  key: string;
  label: string;
  stage: S;
}

/**
 * The header's badges, one per role — except that a title at several companies at the same stage is
 * one badge, "Chief Revenue Officer at 8 companies" (T38b): a person introduced to eight prospects
 * would otherwise stack eight near-identical badges above everything else on the page. The list
 * card under it still names every role with its company. First-seen order; a role on no company is
 * never grouped, since it has no company to count, and two of one title at one company read as
 * that company's, not as "2 companies".
 */
export function roleBadges<S extends string>(
  pipeline: readonly {
    searchId: string;
    searchTitle: string;
    clientName: string | null;
    clientId: string | null;
    stage: S;
  }[],
): RoleBadge<S>[] {
  const groups = new Map<string, { entries: (typeof pipeline)[number][] }>();
  for (const entry of pipeline) {
    const key =
      entry.clientName === null
        ? `role:${entry.searchId}`
        : `title:${JSON.stringify([entry.searchTitle, entry.stage])}`;
    const group = groups.get(key) ?? { entries: [] };
    group.entries.push(entry);
    groups.set(key, group);
  }
  return [...groups.entries()].map(([key, { entries }]) => {
    const first = entries[0]!;
    const companies = new Set(entries.map((entry) => entry.clientId)).size;
    const label =
      companies > 1 ? `${first.searchTitle} at ${companies} companies` : mandateLabel(first);
    return { key, label, stage: first.stage };
  });
}
